import { AppShell } from '@pwa/components/app-shell';
import { ErrorBoundary } from '@pwa/components/error-boundary';
import { PageLoader } from '@pwa/components/page-loader';
import { useAnimatedTheme } from '@pwa/hooks/use-animated-theme';
import { useDocumentTitle } from '@pwa/hooks/use-document-title';
import { type KeyboardShortcut, useKeyboardShortcuts } from '@pwa/hooks/use-keyboard-shortcuts';
import { usePrefersReducedMotion } from '@pwa/hooks/use-prefers-reduced-motion';
import { useCommandPaletteStore } from '@pwa/lib/command-palette-store';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense, useEffect, useMemo } from 'react';

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	component: RootComponent,
});

function RootComponent() {
	// Baseline title — child routes can override this with their own
	// `useDocumentTitle` call. The hook also restores the previous title
	// on unmount, so transient navigations don't pollute the tab.
	useDocumentTitle('Scout');

	const prefersReducedMotion = usePrefersReducedMotion();

	// Toggle a body class so feature components that don't use the hook
	// directly (e.g. third-party widgets) can still key off the preference.
	useEffect(() => {
		if (prefersReducedMotion) {
			document.body.classList.add('reduced-motion');
		} else {
			document.body.classList.remove('reduced-motion');
		}
		return () => {
			document.body.classList.remove('reduced-motion');
		};
	}, [prefersReducedMotion]);

	const { setTheme, resolvedTheme } = useAnimatedTheme();
	const { toggle: toggleCommandPalette } = useCommandPaletteStore();

	const shortcuts = useMemo<ReadonlyArray<KeyboardShortcut>>(
		() => [
			{
				key: 'k',
				modifiers: ['meta'],
				description: 'Open search',
				handler: () => {
					toggleCommandPalette();
				},
			},
			{
				key: 'l',
				modifiers: ['meta'],
				description: 'Toggle theme',
				handler: () => {
					const next = resolvedTheme === 'dark' ? 'light' : 'dark';
					setTheme(next);
				},
			},
		],
		[resolvedTheme, setTheme, toggleCommandPalette],
	);

	useKeyboardShortcuts(shortcuts);

	return (
		<AppShell>
			<ErrorBoundary>
				<Suspense fallback={<PageLoader />}>
					<Outlet />
				</Suspense>
			</ErrorBoundary>
			<TanStackRouterDevtools position='bottom-right' />
			<ReactQueryDevtools buttonPosition='bottom-right' />
		</AppShell>
	);
}
